/**
 * 账号开通API服务
 */

const API_BASE_URL = '/api';

/**
 * 账号开通记录接口
 */
export interface AccountActivation {
  id: number;
  platform_account: string;
  email: string;
  platform: string;
  product_code: string;
  vip_level: number;
  vip_duration: number; // VIP时长（单位：天）
  user_id?: number;
  oneapi_user_id?: number;
  token_quota: number;
  status: 'pending' | 'success' | 'failed';
  error_msg?: string;
  batch_id: string;
  activated_by?: number;
  activated_at?: string;
  notify_sent?: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

/**
 * 账号项
 */
export interface AccountItem {
  platform_account: string;
  email: string;
  token_quota?: number;
  vip_duration?: number; // VIP时长（单位：天）
  notes?: string;
}

/**
 * 导入请求
 */
export interface ImportAccountsInput {
  accounts: AccountItem[];
  batch_name?: string;
  product_code: string;
  platform: string;
}

/**
 * 导入结果统计
 */
export interface ImportStats {
  total: number;
  success: number;
  failed: number;
}

/**
 * 批次状态统计
 */
export interface BatchStats {
  total: number;
  success: number;
  failed: number;
  pending: number;
}

/**
 * 批次状态响应
 */
export interface BatchStatus {
  batch_id: string;
  stats: BatchStats;
  items: AccountActivation[];
}

/**
 * 分页参数
 */
export interface ActivationQueryParams {
  page?: number;
  page_size?: number;
  batch_id?: string;
  status?: 'pending' | 'success' | 'failed';
}

/**
 * 分页响应
 */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * API响应
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * 获取认证token
 */
function getAuthHeaders(): HeadersInit {
  const authStore = localStorage.getItem('auth-storage');
  if (authStore) {
    const { state } = JSON.parse(authStore);
    const token = state?.accessToken;
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
  }
  return {
    'Content-Type': 'application/json',
  };
}

/**
 * 账号开通API服务
 */
export class AccountActivationApiService {
  private readonly baseUrl = `${API_BASE_URL}/admin/account-activations`;

  /**
   * 下载导入模板
   */
  async downloadTemplate(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/template`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '下载模板失败');
    }

    // 获取文件名
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'account_activation_template.csv';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, '');
      }
    }

    // 创建下载链接
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 导入账号
   */
  async importAccounts(input: ImportAccountsInput): Promise<{ batch_id: string; message: string; stats: ImportStats }> {
    const response = await fetch(`${this.baseUrl}/import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || '导入账号失败');
    }

    const result: ApiResponse<{ batch_id: string; message: string; stats: ImportStats }> = await response.json();
    if (!result.success) {
      throw new Error(result.message || result.error || '导入账号失败');
    }

    return result.data!;
  }

  /**
   * 获取批次状态
   */
  async getBatchStatus(batchId: string): Promise<BatchStatus> {
    const response = await fetch(`${this.baseUrl}/batch/${batchId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || '获取批次状态失败');
    }

    const result: ApiResponse<BatchStatus> = await response.json();
    if (!result.success) {
      throw new Error(result.message || result.error || '获取批次状态失败');
    }

    return result.data!;
  }

  /**
   * 获取开通记录列表
   */
  async getActivationList(params?: ActivationQueryParams): Promise<PaginatedResult<AccountActivation>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.batch_id) queryParams.append('batch_id', params.batch_id);
    if (params?.status) queryParams.append('status', params.status);

    const response = await fetch(`${this.baseUrl}?${queryParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || '获取开通记录列表失败');
    }

    const result: ApiResponse<{ data: AccountActivation[]; pagination: { page: number; page_size: number; total: number } }> = await response.json();
    if (!result.success) {
      throw new Error(result.message || result.error || '获取开通记录列表失败');
    }

    return {
      items: result.data?.data || [],
      total: result.data?.pagination?.total || 0,
      page: result.data?.pagination?.page || 1,
      page_size: result.data?.pagination?.page_size || 20,
    };
  }
}

// 导出单例
export const accountActivationApi = new AccountActivationApiService();
